/*
  LASE — random builds showcase.

  A non-interactive 3D viewport that improvises: every few seconds it
  generates a random stack (module count, sizes, order, colours,
  matte/glow) within the same height cap as the configurator, and
  builds it module-by-module. No controls, no picking — pure display.

  Self-contained: three.js comes from jsdelivr's ESM bridge (which
  rewrites bare `three` imports internally), so the section works on
  any page without the configurator's importmap. Render setup mirrors
  the configurator (linear tone mapping, neutral lights, photo-matched
  material response) so both read identically.

  Host contract: <div data-lase-random-builds> containing
    <script type="application/json" data-lrb-data> {models, heightsMm, maxMm, finishes, intervalMs}
    <canvas data-lrb-canvas>
*/
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.170.0/+esm';
import { GLTFLoader } from 'https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/loaders/GLTFLoader.js/+esm';
import { DRACOLoader } from 'https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/loaders/DRACOLoader.js/+esm';
import { RoomEnvironment } from 'https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/environments/RoomEnvironment.js/+esm';

const REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function easeOutCubic (t) { return 1 - Math.pow(1 - t, 3); }
function easeInCubic  (t) { return t * t * t; }
function pick (arr)       { return arr[Math.floor(Math.random() * arr.length)]; }

async function setup (host) {
  const dataTag = host.querySelector('[data-lrb-data]');
  const canvas  = host.querySelector('[data-lrb-canvas]');
  if (!dataTag || !canvas) return;

  let cfg;
  try { cfg = JSON.parse(dataTag.textContent); } catch (e) { return; }
  const sizes = ['S', 'M', 'L'].filter(s => cfg.models && cfg.models[s]);
  if (!sizes.length || !cfg.finishes) return;

  // ---------- renderer / scene ----------
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  // KhronosPBRNeutral — hue-accurate filmic response, matches the
  // configurator so the homepage 3D and the builder read identically.
  renderer.toneMapping = THREE.NeutralToneMapping ?? THREE.LinearToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

  const camera = new THREE.PerspectiveCamera(30, 1, 0.05, 10);
  camera.position.set(0, 0.34, 1.5);
  const camTarget = new THREE.Vector3(0, 0.24, 0);

  const hemi = new THREE.HemisphereLight(0xffffff, 0xf4f4f4, 0.18);
  const key  = new THREE.DirectionalLight(0xffffff, 1.55); key.position.set(0.8, 1.2, 1.0);
  const fill = new THREE.DirectionalLight(0xffffff, 0.45); fill.position.set(-1.0, 0.6, 0.6);
  const rim  = new THREE.DirectionalLight(0xffffff, 0.65); rim.position.set(0, 1.0, -1.2);
  scene.add(hemi, key, fill, rim);

  // Soft contact shadow — radial gradient blob under the stack.
  const shadowCanvas = document.createElement('canvas');
  shadowCanvas.width = shadowCanvas.height = 256;
  const sctx = shadowCanvas.getContext('2d');
  const grad = sctx.createRadialGradient(128, 128, 10, 128, 128, 128);
  grad.addColorStop(0, 'rgba(0,0,0,0.28)');
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  sctx.fillStyle = grad; sctx.fillRect(0, 0, 256, 256);
  const shadow = new THREE.Mesh(
    new THREE.PlaneGeometry(0.48, 0.48),
    new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(shadowCanvas), transparent: true, depthWrite: false })
  );
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = 0.001;
  scene.add(shadow);

  // size to container (before the async model load so the canvas is
  // correctly sized the moment the first frame renders)
  function resize () {
    const r = host.getBoundingClientRect();
    const w = Math.max(1, r.width), h = Math.max(1, r.height);
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  new ResizeObserver(resize).observe(host);
  resize();

  // ---------- models ----------
  // The module GLBs are Draco-compressed (same files the configurator
  // loads) — wire up the decoder before parsing.
  const loader = new GLTFLoader();
  const draco = new DRACOLoader();
  draco.setDecoderPath('https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/libs/draco/gltf/');
  loader.setDRACOLoader(draco);
  const models = {};
  const absolutize = (u) => u && u.startsWith('//') ? 'https:' + u : u;
  host.dataset.lrbState = 'loading';
  await Promise.all(sizes.map(s =>
    loader.loadAsync(absolutize(cfg.models[s])).then(g => {
      const obj = g.scene;
      const box = new THREE.Box3().setFromObject(obj);
      models[s] = { obj, yMin: box.min.y, height: box.max.y - box.min.y };
    }).catch(err => {
      console.warn('[lase-random-builds] model %s failed:', s, err);
    })
  ));
  // Connector — optional; without it every build renders compact.
  let connector = null;
  if (cfg.connector && cfg.connector.model) {
    await loader.loadAsync(absolutize(cfg.connector.model)).then(g => {
      const box = new THREE.Box3().setFromObject(g.scene);
      connector = { obj: g.scene, yMin: box.min.y, height: box.max.y - box.min.y };
    }).catch(err => console.warn('[lase-random-builds] connector failed:', err));
  }
  const conColors = (cfg.connector && cfg.connector.colors) || [];

  const loaded = sizes.filter(s => models[s]);
  host.dataset.lrbState = loaded.length ? 'ready' : 'no-models';
  if (!loaded.length) return;

  // ---------- random recipe ----------
  const MAX = cfg.maxMm || 565;
  const H   = cfg.heightsMm || { S: 70, M: 147, L: 209 };

  function randomBuild () {
    // A build always needs an M/L body — S-only stacks don't assemble
    // (S is a topper, not a base). Seed with one body, fill randomly
    // within the height cap, then shuffle the order.
    const bodies = loaded.filter(s => s !== 'S');
    const mods = [pick(bodies.length ? bodies : loaded)];
    let mm = H[mods[0]];
    const target = 2 + (Math.random() < 0.6 ? 1 : 0);
    while (mods.length < target) {
      const fits = loaded.filter(s => mm + H[s] <= MAX);
      if (!fits.length) break;
      const s = pick(fits);
      mods.push(s); mm += H[s];
    }
    if (mods.length < 2) return randomBuild();
    for (let i = mods.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [mods[i], mods[j]] = [mods[j], mods[i]];
    }

    // exactly one glow module (it's a lamp), colours: mono or duo
    const glowIdx = Math.floor(Math.random() * mods.length);
    const matteColors = cfg.finishes.matte.colors;
    const glowColors  = cfg.finishes.glow.colors;
    const mono = Math.random() < 0.55;
    const cA = pick(matteColors).id;
    let cB = pick(matteColors).id;
    while (!mono && cB === cA && matteColors.length > 1) cB = pick(matteColors).id;

    const modules = mods.map((size, i) => {
      const isGlow = i === glowIdx;
      const colorId = mono ? cA : (isGlow ? cA : cB);
      const palette = isGlow ? glowColors : matteColors;
      const c = palette.find(x => x.id === colorId) || palette[0];
      return { size, glow: isGlow, ext: c.ext, int: c.int || c.ext };
    });

    // Roughly half the builds show semi-exploded — gaps open up and
    // the connectors peek out. Connector colour: white-biased, but it
    // can borrow any matte colour from the palette.
    const semi = connector && Math.random() < 0.5;
    const conColor = (Math.random() < 0.55 || !conColors.length)
      ? '#E8E6E0'
      : pick(conColors).ext;
    return { modules, gap: semi ? 0.015 : 0, conColor };
  }

  // ---------- build a stack group ----------
  function tint (clone, m) {
    clone.traverse(o => {
      if (!o.isMesh || !o.material) return;
      const name = o.material.name || '';
      if (!name.startsWith('colorable')) return;
      o.material = o.material.clone();
      const mat = o.material;
      const isInt = name.includes('int');
      mat.color.set(isInt ? m.int : m.ext);
      if ('metalness' in mat) mat.metalness = 0;
      if ('roughness' in mat) mat.roughness = m.glow ? (isInt ? 0.5 : 0.45) : (isInt ? 0.95 : 0.88);
      if ('envMapIntensity' in mat) mat.envMapIntensity = m.glow ? 0.55 : (isInt ? 0.12 : 0.20);
      if (m.glow) { mat.transparent = true; mat.opacity = isInt ? 0.94 : 0.98; }
    });
  }

  function makeStack (recipe) {
    const group = new THREE.Group();
    let y = 0;
    const pivots = [];

    function addPivot (clone, yBase, yOffset) {
      const pivot = new THREE.Group();
      clone.position.y = yOffset;
      pivot.add(clone);
      pivot.position.y = yBase;
      pivot.scale.setScalar(REDUCED ? 1 : 0.001);
      group.add(pivot);
      pivots.push(pivot);
    }

    recipe.modules.forEach((m, i) => {
      const src = models[m.size];
      const clone = src.obj.clone(true);
      tint(clone, m);
      addPivot(clone, y, -src.yMin);
      y += src.height;

      // Semi view: open the gap and seat a connector at its centre —
      // the TPU joint pokes into both modules, exactly like the
      // configurator's semi mode.
      const isLast = i === recipe.modules.length - 1;
      if (!isLast && recipe.gap > 0 && connector) {
        const cn = connector.obj.clone(true);
        tint(cn, { ext: recipe.conColor, int: recipe.conColor, glow: false });
        addPivot(cn, y + recipe.gap / 2, -(connector.yMin + connector.height / 2));
      }
      if (!isLast) y += recipe.gap;
    });

    group.userData.pivots = pivots;
    group.userData.height = y;
    return group;
  }

  // ---------- animation loop ----------
  const stage = new THREE.Group();
  scene.add(stage);
  let current = null, leaving = null;
  let swapTimer = 0;
  const INTERVAL = Math.max(2500, cfg.intervalMs || 4500);
  const POP = 420, STAGGER = 140, OUT = 260;

  function spawn () {
    if (current) { leaving = current; leaving.userData.outAt = clock.elapsedTime; }
    current = makeStack(randomBuild());
    current.userData.inAt = clock.elapsedTime;
    stage.add(current);
  }

  const clock = new THREE.Clock();
  let running = false, raf = 0;

  function frame () {
    raf = requestAnimationFrame(frame);
    const dt = clock.getDelta();
    const t  = clock.elapsedTime;

    if (!REDUCED) stage.rotation.y += dt * 0.28;

    if (current) {
      const since = t - current.userData.inAt;
      current.userData.pivots.forEach((p, i) => {
        const k = REDUCED ? 1 : Math.max(0, Math.min(1, (since - i * STAGGER / 1000) / (POP / 1000)));
        p.scale.setScalar(0.001 + 0.999 * easeOutCubic(k));
      });
      // frame the stack: ease the camera target toward its mid-height
      camTarget.y += ((current.userData.height / 2 + 0.02) - camTarget.y) * Math.min(1, dt * 3);
      camera.lookAt(camTarget);
    }
    if (leaving) {
      const k = Math.min(1, (t - leaving.userData.outAt) / (OUT / 1000));
      leaving.scale.setScalar(Math.max(0.001, 1 - easeInCubic(k)));
      if (k >= 1) { stage.remove(leaving); leaving = null; }
    }

    swapTimer += dt * 1000;
    if (swapTimer >= INTERVAL) { swapTimer = 0; spawn(); }

    renderer.render(scene, camera);
  }

  function start () { if (!running) { running = true; clock.getDelta(); frame(); } }
  function stop  () { if (running)  { running = false; cancelAnimationFrame(raf); } }

  // run only while on screen
  new IntersectionObserver(es => {
    es.forEach(e => e.isIntersecting ? start() : stop());
  }, { rootMargin: '80px' }).observe(host);

  spawn();
  start();
}

function init () {
  document.querySelectorAll('[data-lase-random-builds]').forEach(h => {
    if (h.dataset.lrbBound) return;
    h.dataset.lrbBound = '1';
    setup(h);
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
document.addEventListener('shopify:section:load', init);
